import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useModal } from "react-modal-hook";
import { buildGuestWorkflowURL } from "common/utils/url";
import {
  AccessControlEntityType,
  AccessControlPolicy,
  GetWorkflowQuery,
  GroupRoleType,
  useUpdateWorkflowCollaboratorsMutation,
  useWorkflowCollaboratorsQuery,
  WorkflowCollaboratorFieldsFragment,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { CommonModal } from "../Common/CommonModal";
import { Loading } from "../Common/Icons/Loading";
import Modal from "../Common/Modal";
import { ShareForm } from "../Common/ShareForm/ShareForm";
import { Collaborator, PermissionOption } from "../Common/ShareForm/types";

const buildCollaboratorFromFragment = (
  collab: WorkflowCollaboratorFieldsFragment,
): Collaborator => {
  const entity = collab.publicUserOrInvite;

  switch (entity.__typename) {
    case "PublicUser": {
      const name = `${entity.firstName} ${entity.lastName}`.trim();
      return {
        id: collab.entityId,
        name,
        description: entity.email,
        permission: collab.policy,
        entityType: "publicUser",
        status: entity.status,
        role: entity.userGroups?.[0]?.name,
        userGroupId: entity.userGroups?.[0]?.id,
        email: entity.email,
      };
    }
    case "PublicUserInvite": {
      return {
        id: collab.entityId,
        name: entity.email,
        description: entity.email,
        permission: collab.policy,
        entityType: "publicUserInvite",
        status: "inviteSent",
        role: entity.userGroup.name,
        userGroupId: entity.userGroup.id,
        email: entity.email,
      };
    }
  }
};

const mapCollaboratorsFromData = (
  collaborators: WorkflowCollaboratorFieldsFragment[],
): Collaborator[] => {
  if (!collaborators) return [];

  const permissionRank: Record<string, number> = {
    createResource: 3,
    updateResource: 2,
    viewResource: 1,
  };

  const collaboratorMap = new Map<string, Collaborator>();

  collaborators.forEach(collab => {
    const built = buildCollaboratorFromFragment(collab);

    const existing = collaboratorMap.get(collab.entityId);
    const currentRank = permissionRank[collab.policy] || 0;
    const existingRank = existing
      ? permissionRank[existing.permission] || 0
      : -1;

    if (currentRank > existingRank) {
      collaboratorMap.set(collab.entityId, built);
    }
  });

  return Array.from(collaboratorMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
};

export interface ShareWorkflowModalProps {
  workflow: Pick<NonNullable<GetWorkflowQuery["workflow"]>, "id">;
  onClose: () => void;
  clipboard?: Pick<Clipboard, "writeText">;
  onInvite?: (collaborators: Collaborator[]) => void;
  onRemove?: (collaboratorId: string) => void;
}

const WORKFLOW_PERMISSION_OPTIONS: PermissionOption[] = [
  { value: "createResource", label: "Can create" },
  { value: "updateResource", label: "Can update" },
  { value: "viewResource", label: "Can view" },
];

const buildCollaboratorInput = (c: Collaborator) => {
  switch (c.entityType) {
    case "emailInvite":
      if (!c.userGroupId) {
        throw new Error("Email invite missing required userGroupId");
      }
      return {
        email: c.email,
        userGroupId: c.userGroupId,
        policy: AccessControlPolicy.MEMBERSHIP,
      };
    case "publicUserInvite":
      return {
        entityId: c.id,
        entityType: AccessControlEntityType.PUBLIC_USER_INVITE,
        policy: AccessControlPolicy.MEMBERSHIP,
      };
    case "publicUser":
      return {
        entityId: c.id,
        entityType: AccessControlEntityType.PUBLIC_USER,
        policy: AccessControlPolicy.MEMBERSHIP,
      };
  }
};

export const ShareWorkflowModalContent = ({
  workflow,
  onClose,
  clipboard = navigator.clipboard,
}: Omit<ShareWorkflowModalProps, "onInvite" | "onRemove">) => {
  const { account } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const { data: workflowData, loading: isLoading } =
    useWorkflowCollaboratorsQuery({
      variables: { workflowId: workflow.id },
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    });

  const [updateWorkflowCollaborators, { loading: isSubmitting }] =
    useUpdateWorkflowCollaboratorsMutation();

  const initialCollaborators = mapCollaboratorsFromData(
    workflowData?.workflow?.collaborators ?? [],
  );

  const currentCollaboratorsRef = useRef<Collaborator[]>([]);
  const [hasIncompleteInvites, setHasIncompleteInvites] = useState(false);
  const [hasNewEmailInvites, setHasNewEmailInvites] = useState(false);

  const handleStateChange = useCallback((collaborators: Collaborator[]) => {
    currentCollaboratorsRef.current = collaborators;
    setHasIncompleteInvites(
      collaborators.some(c => c.entityType === "emailInvite" && !c.userGroupId),
    );
    setHasNewEmailInvites(
      collaborators.some(c => c.entityType === "emailInvite"),
    );
  }, []);

  const roleOptions = useMemo(
    () =>
      (account?.userGroups ?? [])
        .filter(g => g.memberType === GroupRoleType.PUBLIC_USER)
        .map(g => ({ id: g.id, name: g.name })),
    [account?.userGroups],
  );

  const handleCopyLink = useCallback(async () => {
    const url = buildGuestWorkflowURL({
      subdomain: account!.subdomain,
      workflowId: workflow.id,
    });
    await clipboard.writeText(url);
    addSuccessToast("Link copied to clipboard");
  }, [account, workflow.id, clipboard, addSuccessToast]);

  const handleSubmit = useCallback(async () => {
    try {
      await updateWorkflowCollaborators({
        variables: {
          data: {
            workflowId: workflow.id,
            collaborators: currentCollaboratorsRef.current.map(
              buildCollaboratorInput,
            ),
          },
        },
      });

      track("Shared Workflow", {
        workflowId: workflow.id,
      });

      addSuccessToast(`This workflow has been updated successfully`);
      onClose();
    } catch {
      addErrorToast(`Failed to update workflow`);
    }
  }, [
    workflow.id,
    updateWorkflowCollaborators,
    addSuccessToast,
    addErrorToast,
    onClose,
  ]);

  const buttonText = isSubmitting
    ? "Saving..."
    : hasNewEmailInvites
      ? "Invite"
      : "Share";

  return (
    <CommonModal
      title="Share workflow"
      size="large"
      onCancel={onClose}
      primaryButtonText={buttonText}
      onSubmit={handleSubmit}
      disabled={isSubmitting || isLoading || hasIncompleteInvites}
      secondaryButtons={[
        {
          text: "Copy link",
          onClick: handleCopyLink,
        },
      ]}
      content={
        isLoading ? (
          <Loading size="medium" />
        ) : (
          <ShareForm
            searchPlaceholder="Add email address"
            sectionLabel="Who has access"
            infoText={{
              text: `All Forerunner team members can view this workflow.`,
              linkText: "Add a team member",
              linkHref: "/settings/users/team",
            }}
            permissionOptions={WORKFLOW_PERMISSION_OPTIONS}
            initialCollaborators={initialCollaborators}
            onStateChange={handleStateChange}
            buttonText="Add"
            includeUsers={false}
            includePublicUsers
            includeInvites
            showPermissionOptions={false}
            roleOptions={roleOptions}
          />
        )
      }
    />
  );
};

export const useShareWorkflowModal = ({
  workflow,
  clipboard = navigator.clipboard,
}: Omit<ShareWorkflowModalProps, "onClose" | "onInvite" | "onRemove">) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <ShareWorkflowModalContent
          workflow={workflow}
          onClose={hideModal}
          clipboard={clipboard}
        />
      </Modal>
    ),
    [workflow, clipboard],
  );

  return [showModal, hideModal] as const;
};
